import { useContext, useState } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import {
  ErrorDialogContext,
  errorHandledAsync,
  // errorHandledSync,
} from "@/shared/context/error-context";
import { GetFinanceAccountsService } from "@/features/accounts/finance/services/get-accounts-service";
import { SynchronizeAccountsService } from "@/features/accounts/finance/services/synchronize-accounts-service";
import { usePutFinanceAccount } from "@/features/accounts/finance/hooks/use-put-accounts";
import { GetPaymentAccountsService } from "@/features/accounts/payment/scraping/services/get-accounts-service";
import { UpdatePaymentApiAccountsListService } from "@/features/accounts/payment/api/services/update-accounts-list-service";
import { AddPaymentAccountsService } from "@/features/accounts/payment/scraping/services/add-accounts-service";
import { useUpdatePaymentAccounts } from "@/features/accounts/payment/scraping/hooks/use-update-accounts";
import { useDeletePaymentAccounts } from "@/features/accounts/payment/scraping/hooks/use-delete-accounts";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { MoneyLinkException } from "@/shared/utils/custom-exception";
import { ErrorType } from "@/shared/config/error-type";


export interface AccountsInfo {
  accountGroupId: string;
  mlinkAccountId: string;
  mlinkId: string;
  accountKindName: string;
  accountNo: string;
  selectedStatus: number;
  //accountType: "finance" | "creca" | "payment";
  passbookType: string;
  userInputName: string;
  scrapeType: number; // 0：なし、1：インストール版スクレイピング（対象なし）、2：サーバ版スクレイピング、3：API版
}

interface Props {
  onTapCompleteModal: (accounts: AccountsInfo[]) => void;
}

export const useSyncAccounts = ({onTapCompleteModal}: Props) => {
  const getFinanceAccountsService = new GetFinanceAccountsService();
  const synchronizeAccountsService = new SynchronizeAccountsService();
  const getPaymentAccountsService = new GetPaymentAccountsService();
  const updatePaymentApiAccountsListService = new UpdatePaymentApiAccountsListService();
  const addPaymentAccountsService = new AddPaymentAccountsService();
  const { updatePaymentAccounts } = useUpdatePaymentAccounts();
  const { deletePaymentAccounts } = useDeletePaymentAccounts();

  const { updateFinanceAccounts } = usePutFinanceAccount();
  // const { updatePaymentAccounts } = useUpdatePaymentAccounts();

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const [isActiveRegisterButton, setIsActiveRegisterButton] = useState(false);

  // 口座の追加オプションの押下
  // passbookTypeごとに
  const onClickSyncAccounts = async (
    passbookType: string,
    accountGroupId: string,
    mlinkAccountId: string,
    mlinkId: string,
    scrapeType: number
  ) => {
    loadingCtx.setIsLoading(true);
    // let list: AccountsInfo[] = [];
    try {
      // 銀行API
      if (passbookType === "01") {
        try {
          await synchronizeAccountsService.synchronizeAccounts({
            account_group_id: accountGroupId,
            finance_account_id: mlinkAccountId,
            is_sync_bank: true,
          });
        } catch (error) {
          // 銀行APIの口座同期でエラーが発生した場合は、linkstorageまでの同期で再度実行する
          if (
            error instanceof MoneyLinkException
            && error.error.props.reason === ErrorType.REAUTH_FOR_SYNC
          ) {
            await synchronizeAccountsService.synchronizeAccounts({
              account_group_id: accountGroupId,
              finance_account_id: mlinkAccountId,
              is_sync_bank: false,
            });
          } else {
            throw error;
          }
        }
        const resFinanceAccounts = await getFinanceAccountsService.getFinanceAccounts({
          account_group_id: accountGroupId,
          finance_account_id: mlinkAccountId,
          use_account_group_id: false,
        });
        // 返却値の中で，financeのselected_statusが0のもののみ抽出して返却する
        const resAccounts: AccountsInfo[] = resFinanceAccounts.flatMap(financeAccount => 
          financeAccount.finance_list
            .filter(finance => (finance.finance_type_code === 1 || finance.finance_type_code === 2) && finance.selected_status === 0)  // 普通・当座口座で未選択状態のもの
            .map(finance => ({
              accountGroupId: accountGroupId,
              mlinkAccountId: financeAccount.finance_account_id,
              mlinkId: finance.finance_id,
              accountKindName: finance.account_display_name,
              accountNo: finance.finance_number,
              selectedStatus: finance.selected_status,
              passbookType: financeAccount.industry_code,
              userInputName: "",
              scrapeType: 0,
        })));
        // resAccountsが0件ならば新しい口座が見つからないメッセージを出す
        if (resAccounts.length === 0) {
          setError(new MoneyLinkWarning({
            title: "口座の追加",
            message: "新しい口座が見つかりませんでした。\nすべての口座が既に追加されている可能性があります。",
          }));
          return;
        }
        return resAccounts;
      }
      // Look口座
      else if (passbookType === "03" || passbookType === "10") {
        // スクレイピングの口座同期は別hookにて，ここではAPI版のみ
        if (scrapeType === 3) { // API版
          await updatePaymentApiAccountsListService.updatePaymentAccountsList({
            account_group_id: accountGroupId,
            payment_account_id: mlinkAccountId,
            payment_id: mlinkId,
          });
        }
        // サーバー側で口座を追加する処理(スクレイピング，API共通)
        const resAddAccounts = await addPaymentAccountsService.addPaymentAccounts({
          account_group_id: accountGroupId,
          payment_account_id: mlinkAccountId,
          payment_id: mlinkId,
        });
        // TODO: 定数化した方がいいと思う
        if (resAddAccounts.status === "non_add_accountslist") {
          setError(new MoneyLinkWarning({
            title: "口座の追加",
            message: "新しい口座が見つかりませんでした。\nすべての口座が既に追加されている可能性があります。",
          }));
          return;
        }

        // 口座一覧取得
        const resPaymentAccounts = await getPaymentAccountsService.getPaymentAccounts({
          account_group_id: accountGroupId,
          payment_account_id: mlinkAccountId,
          industry_code: passbookType,
        });
        // 返却値の中で，payment_idが一致していないものみ抽出して返却する
        const resAccounts: AccountsInfo[] = resPaymentAccounts.flatMap(paymentAccount => 
          paymentAccount.goods_list
            .filter(payment => payment.payment_id !== mlinkId)
            .map(payment => ({
              accountGroupId: accountGroupId,
              mlinkAccountId: paymentAccount.payment_account_id,
              mlinkId: payment.payment_id,
              accountKindName: payment.account_kind_name,
              accountNo: payment.account_no,
              selectedStatus: 0,
              passbookType: paymentAccount.industry_code,
              userInputName: "",
              scrapeType: paymentAccount.scrape_type,
        })));
        return resAccounts;
      }
    } catch (error) {
      console.log("onClickSyncAccounts", error);
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // ボタン押下 → チェックされている要素に対してAPI実行
  const onClickAdd = async (
    lists: AccountsInfo[]
  ) => {
    loadingCtx.setIsLoading(true);
    // 各API関数は errorHandledAsync でラップ済み、エラー時は undefined を返す
    let hasError = false;

    for (const list of lists) {
      let result: unknown;

      // 銀行API
      if (list.passbookType === "01") {
        // 銀行API口座は選択された口座のみ選択状態に更新
        if (list.selectedStatus === 1) {
          result = await updateFinanceAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
            1,
            undefined, // isAggregate
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
            false, // useAccountGroupId
          );
        } else {
          result = true; // 処理不要の場合は成功扱い
        }
      }
      // Look口座
      else if (list.passbookType === "03" || list.passbookType === "10") {
        // Look口座は選択された&口座名を変更した口座は更新、選択されなかった口座は削除
        if (list.selectedStatus === 1 && list.userInputName !== "") {
          result = await updatePaymentAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
          );
        } else if (list.selectedStatus !== 1) {
          result = await deletePaymentAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
          );
        } else {
          result = true;
        }
      }

      // エラー発生時（undefined）はフラグを立てて続行（全件処理する）
      if (result === undefined) {
        hasError = true;
      }
    }

    loadingCtx.setIsLoading(false);

    if (hasError) {
      // エラー時はカスタムメッセージを表示（ホーム画面に戻り，正常終了した口座は見せる必要がある）
      setError(new MoneyLinkWarning({
        title: "エラー",
        message: "登録操作が完了しなかった口座があります",
      }));
    }

    // エラーがあっても成功した口座でホーム画面に戻る
    onTapCompleteModal(lists.filter((item) => item.selectedStatus === 1));
  };

  // 登録キャンセル（削除APIが失敗してもダイアログは表示せず業務続行）
  const cancelAdd = async (
    lists: AccountsInfo[]
  ) => {
    for (const list of lists) {
      if (list.passbookType === "03" || list.passbookType === "10") {
        await deletePaymentAccounts(
          list.accountGroupId,
          list.mlinkAccountId,
          list.mlinkId,
        );
      }
    }
    // エラーが発生してもダイアログを表示しないようにクリア
    setError(undefined);
  };

  const resetLocalStorageForSync = () => {
    localStorage.removeItem("institutionName");
    localStorage.removeItem("serviceType");
    localStorage.removeItem("serviceName");
    localStorage.removeItem("branchName");
  };

  return {
    isActiveRegisterButton,
    setIsActiveRegisterButton,
    onClickSyncAccounts: errorHandledAsync(onClickSyncAccounts, setError),
    onClickAdd: errorHandledAsync(onClickAdd, setError),
    cancelAdd: errorHandledAsync(cancelAdd, setError),
    resetLocalStorageForSync,
  };
};